import {
  Outlet,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import {
  QueryClientProvider,
  type QueryClient as QC,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "sonner";
import { useResolvedTheme } from "@/lib/theme-store";
import { queryClient } from "@/main";
import { ConfirmDialogHost } from "@/components/ui/ConfirmDialog";
import { TooltipProvider } from "@/components/ui/Tooltip";

/**
 * Contexto disponible para todas las rutas · cargado por `RouterProvider`
 * en `main.tsx` o por test/storybook setups. Hoy solo expone el QueryClient
 * para que loaders/route guards puedan disparar prefetches con `ensureQuery
 * Data`. Si necesitamos auth context-bound aquí lo agregamos.
 */
export interface RouterContext {
  queryClient: QC;
}

// Devtools de TanStack (Router + Query) · ocultos por defecto. Para volver a
// verlos en desarrollo: correr con `VITE_DEVTOOLS=true` (p. ej. en `.env.local`).
const SHOW_DEVTOOLS = import.meta.env.DEV && import.meta.env.VITE_DEVTOOLS === "true";

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  // Antes vivía en `app/layout.tsx` como `export const metadata`. TanStack
  // Router permite definir `<head>` por ruta, así que podemos ir override­
  // ando títulos en routes hijas conforme las migremos.
  head: () => ({
    meta: [
      { title: "Portal de proveedores" },
      {
        name: "description",
        content: "Portal para la gestión de pagos a proveedores.",
      },
    ],
  }),
});

/**
 * Layout raíz · monta los providers globales (Query + Toaster + Tooltip).
 * El modo oscuro/claro ya no lo resuelve Mantine (`ColorSchemeScript` +
 * `MantineProvider`) sino `lib/theme-store.ts` (Zustand+persist, Etapa 0) +
 * el script anti-flash inline de `index.html`, que aplica la clase `dark`
 * en `<html>` ANTES de que React monte.
 */
function RootLayout() {
  const resolvedTheme = useResolvedTheme();
  return (
    <>
      {/* Toasts estilo "card + acento lateral" (opción B de la revisión UX):
          superficie neutra del tema (bg-card/border-border · se adapta al
          modo oscuro) con barra izquierda de 3px cuyo color semántico lo pone
          cada wrapper de `lib/toast.tsx` vía style inline. Sin `richColors`:
          los verdes/rojos genéricos de sonner no eran de la paleta del portal.
          `offset.top` = header fijo (56px) + margen, para que el primer
          toast no se encime con la barra naranja. */}
      <Toaster
        position="top-right"
        theme={resolvedTheme}
        closeButton
        gap={10}
        offset={{ top: 68 }}
        mobileOffset={{ top: 64 }}
        toastOptions={{
          classNames: {
            toast: "!rounded-md !border !border-border !border-l-[3px] !bg-card !text-card-foreground !shadow-lg",
            title: "!text-[13px] !font-semibold !text-card-foreground",
            description: "!text-xs !text-muted-foreground",
          },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <Outlet />
          {/* Host único de `useConfirmDialog()` (Etapa 2) · reemplazo de `openConfirmModal`. */}
          <ConfirmDialogHost />
        </TooltipProvider>
        {SHOW_DEVTOOLS ? <ReactQueryDevtools buttonPosition="bottom-left" /> : null}
      </QueryClientProvider>
      {SHOW_DEVTOOLS ? <TanStackRouterDevtools position="bottom-right" /> : null}
    </>
  );
}
